import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Toaster } from "sonner";
import SmoothScroll from "@/components/site/SmoothScroll";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import AmbientLayer from "@/components/site/AmbientLayer";
import Feathers from "@/components/site/Feathers";
import { Reveal } from "@/lib/motion";

const LEGACY_ITEMS = [
  "Memories and life stories", "Photographs and video", "Voice recordings", "Family history",
  "Personal philosophy", "Values and beliefs", "Important relationships", "Creative work",
  "Messages for loved ones", "Future milestone messages", "Thoughts about inheritance",
  "Instructions about privacy and access", "Guidance for future generations",
];

const PEOPLE = ["Children", "Grandchildren", "Partners", "Friends", "Executors", "Trustees", "Attorneys", "Advisors"];

const CHOICES = [
  "You choose what is recorded.",
  "You choose what is private.",
  "You choose who may receive access.",
  "You choose what should survive.",
  "You choose what should not.",
];

const STAGES = [
  {
    era: "In your 20s and 30s",
    title: "Capture your story as it develops.",
    body: "Preserve early experiences, creative work, family history, personal philosophy, and the people who helped shape you.",
  },
  {
    era: "In your 40s and 50s",
    title: "Add the shape of a life in motion.",
    body: "Relationships, family experiences, career history, important lessons, values, photographs, stories for children, and thoughts about the future.",
  },
  {
    era: "In your 60s and beyond",
    title: "Deepen the archive.",
    body: "Record the stories behind important decisions, family traditions, inheritance intentions, personal guidance, and the principles you hope will continue through future generations.",
  },
];

const Overline = ({ children }) => (
  <p className="text-[11px] uppercase tracking-[0.4em] text-es-accent mb-6">{children}</p>
);

const CrossfadeBand = ({ base, clean, alt, testid }) => (
  <div data-testid={testid} className="relative w-full overflow-hidden">
    <div className="relative w-full h-[34vh] sm:h-[42vh] lg:h-[52vh]">
      <img src={base} alt={alt} className="absolute inset-0 w-full h-full object-cover" />
      <motion.img
        src={clean}
        alt={alt}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: false, amount: 0.35 }}
        transition={{ duration: 5, ease: "easeInOut" }}
        className="absolute inset-0 w-full h-full object-cover"
      />
    </div>
  </div>
);

const HERO_SHOTS = ["/hiw-hero-1.jpg", "/hiw-hero-2.jpg", "/hiw-hero-3.jpg"];

export default function HowItWorks() {
  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    const t = setInterval(() => setHeroIdx((p) => (p + 1) % HERO_SHOTS.length), 5500);
    return () => clearInterval(t);
  }, []);

  return (
    <SmoothScroll>
      <div className="App bg-es-bg text-es-fg relative">
        <div className="grain" />
        <AmbientLayer />
        <Feathers />
        <Header />

        <main>
          {/* HERO */}
          <section id="how-top" data-testid="how-hero" className="relative min-h-[92vh] flex items-center overflow-hidden">
            {/* full-bleed crossfading imagery */}
            <div data-testid="how-hero-images" className="absolute inset-0">
              {HERO_SHOTS.map((src, i) => (
                <motion.img
                  key={src}
                  src={src}
                  alt="Generations of a family sharing a quiet moment at golden hour"
                  initial={false}
                  animate={{ opacity: i === heroIdx ? 1 : 0 }}
                  transition={{ duration: 2.2, ease: "easeInOut" }}
                  className="absolute inset-0 w-full h-full object-cover object-[74%_center] sm:object-[65%_center] lg:object-center"
                />
              ))}
              {/* legibility scrims — keep the left/text side readable */}
              <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--es-bg)_0%,var(--es-bg)_30%,rgba(249,248,246,0.55)_50%,transparent_78%)]" />
              <div className="absolute inset-0 bg-gradient-to-t from-es-bg/75 via-transparent to-es-bg/25" />
            </div>

            <div className="relative z-10 mx-auto max-w-[1400px] w-full px-6 lg:px-12 pt-40 pb-20">
              <Reveal>
                <Overline>How EverSoul works</Overline>
                <h1 className="font-serif font-light tracking-tight leading-[0.98] text-[clamp(2.6rem,7vw,6rem)] max-w-4xl text-balance">
                  A legacy built over time.
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="mt-8 max-w-xl space-y-5">
                  <p className="text-base sm:text-lg text-es-fg/80 leading-relaxed">
                    EverSoul is designed to help you thoughtfully preserve the memories, relationships,
                    values, intentions, and personal history that define your life. The process can begin
                    at any age and evolve with you over time.
                  </p>
                  <p className="text-base sm:text-lg text-es-fg/80 leading-relaxed">
                    Some people begin by recording stories and important memories. Others begin with family
                    relationships, personal values, photographs, creative work, or thoughts about the future.
                    Later in life, EverSoul can become increasingly connected to inheritance intentions and the
                    trusted people who help carry those wishes forward.
                  </p>
                </div>
              </Reveal>
            </div>
          </section>

          {/* BEGIN WITH YOUR STORY */}
          <section data-testid="how-begin" className="relative py-24 lg:py-36 bg-es-surface">
            <div className="mx-auto max-w-[1400px] px-6 lg:px-12 grid lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-5">
                <Reveal>
                  <Overline>Step one</Overline>
                  <h2 className="font-serif font-light tracking-tight leading-[1.02] text-[clamp(2rem,4.4vw,3.6rem)]">
                    Begin with your story.
                  </h2>
                </Reveal>
              </div>
              <div className="lg:col-span-6 lg:col-start-7">
                <Reveal delay={0.1}>
                  <p className="text-base sm:text-lg text-es-muted-fg leading-relaxed">
                    Your EverSoul journey begins through guided conversations designed to understand the people,
                    experiences, values, and moments that have shaped your life.
                  </p>
                  <p className="mt-6 font-serif italic text-es-fg text-xl sm:text-2xl leading-snug">
                    You decide what you want to explore, what you want to preserve, and what remains private.
                  </p>
                </Reveal>
              </div>
            </div>
          </section>

          <CrossfadeBand base="/hiw-album.jpg" clean="/hiw-album-clean.jpg" alt="An open photo album with treasured photographs, books, and morning light" testid="how-band-album" />

          {/* BUILD YOUR PERSONAL LEGACY */}
          <section data-testid="how-build" className="relative py-24 lg:py-36">
            <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
              <Reveal>
                <Overline>Step two</Overline>
                <h2 className="font-serif font-light tracking-tight leading-[1.02] text-[clamp(2rem,4.4vw,3.6rem)] max-w-2xl">
                  Build your personal legacy.
                </h2>
                <p className="mt-6 max-w-xl text-es-muted-fg leading-relaxed">
                  Over time, your EverSoul can grow to include the many forms your life has taken.
                </p>
              </Reveal>
              <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-es-border">
                {LEGACY_ITEMS.map((item, i) => (
                  <Reveal key={item} delay={(i % 3) * 0.05}>
                    <div className="border-r border-b border-es-border p-6 lg:p-7 h-full group hover:bg-es-surface transition-colors duration-500">
                      <span className="text-[11px] tracking-[0.3em] text-es-accent">{String(i + 1).padStart(2, "0")}</span>
                      <p className="mt-4 font-serif font-light text-es-fg text-[clamp(1.2rem,2vw,1.6rem)] tracking-tight leading-snug">{item}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* CONNECT THE PEOPLE */}
          <section data-testid="how-people" className="relative py-24 lg:py-36 bg-es-surface">
            <div className="mx-auto max-w-[1400px] px-6 lg:px-12 grid lg:grid-cols-12 gap-10">
              <div className="lg:col-span-5">
                <Reveal>
                  <Overline>Step three</Overline>
                  <h2 className="font-serif font-light tracking-tight leading-[1.02] text-[clamp(2rem,4.4vw,3.6rem)]">
                    Connect the people who matter.
                  </h2>
                  <p className="mt-6 text-es-muted-fg leading-relaxed max-w-md">
                    EverSoul can help you identify the individuals who may have a role in your legacy — and
                    different people may ultimately have different levels of access, depending upon your wishes.
                  </p>
                </Reveal>
              </div>
              <div className="lg:col-span-6 lg:col-start-7 self-center">
                <div className="flex flex-wrap gap-3">
                  {PEOPLE.map((p, i) => (
                    <Reveal key={p} delay={i * 0.04}>
                      <span className="inline-block rounded-full border border-es-border px-5 py-2.5 text-es-fg text-[15px] tracking-wide bg-es-bg/50">
                        {p}
                      </span>
                    </Reveal>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* CHANGE WITH YOU */}
          <section data-testid="how-evolve" className="relative py-24 lg:py-36">
            <div className="mx-auto max-w-[1400px] px-6 lg:px-12 grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-6">
                <Reveal>
                  <Overline>An evolving record</Overline>
                  <h2 className="font-serif font-light tracking-tight leading-[1.02] text-[clamp(2rem,4.4vw,3.6rem)] max-w-xl">
                    Your legacy can change with you.
                  </h2>
                </Reveal>
              </div>
              <div className="lg:col-span-5 lg:col-start-8">
                <Reveal delay={0.1}>
                  <p className="font-serif italic text-es-fg text-xl sm:text-2xl leading-snug">
                    The person you are at thirty may not be the person you are at sixty.
                  </p>
                  <p className="mt-6 text-es-muted-fg leading-relaxed">
                    EverSoul is designed to evolve. You can revisit memories, revise instructions, add new
                    relationships, record new perspectives, and continue shaping how your story should ultimately
                    be understood.
                  </p>
                </Reveal>
              </div>
            </div>
          </section>

          <CrossfadeBand base="/hiw-tree.jpg" clean="/hiw-tree-clean.jpg" alt="A single tree on a hill at golden hour, growing over time" testid="how-band-tree" />

          {/* A PROCESS DESIGNED AROUND YOU */}
          <section data-testid="how-control" className="relative py-28 lg:py-40 bg-[#0F1012] text-[#F9F8F6] overflow-hidden">
            <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
              <Reveal>
                <p className="text-[11px] uppercase tracking-[0.4em] text-[#C5B498] mb-6">A process designed around you</p>
                <h2 className="font-serif font-light tracking-tight leading-[1.05] text-[clamp(2rem,4.4vw,3.6rem)] max-w-3xl text-balance">
                  There does not need to be one way to build an EverSoul.
                </h2>
                <p className="mt-6 max-w-2xl text-[#9A9C9F] leading-relaxed">
                  Depending upon your preferences and the depth of the experience you choose, the process may
                  include guided digital experiences, personal conversations, professional participation, or
                  combinations of these. The important principle is simple.
                </p>
              </Reveal>
              <div className="mt-14 border-t border-[#2A2C31]">
                {CHOICES.map((c, i) => (
                  <Reveal key={c} delay={i * 0.06}>
                    <div className="flex items-baseline gap-6 border-b border-[#2A2C31] py-6">
                      <span className="text-[11px] tracking-[0.3em] text-[#C5B498] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                      <p className="font-serif font-light text-[clamp(1.4rem,3vw,2.4rem)] tracking-tight">{c}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.1}>
                <p className="mt-10 text-[#e6e2d8] text-lg">You remain in control of the process.</p>
              </Reveal>
            </div>
          </section>

          {/* DIFFERENT LIVES */}
          <section data-testid="how-stages" className="relative py-24 lg:py-36">
            <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
              <Reveal>
                <Overline>Different lives, different starting points</Overline>
                <h2 className="font-serif font-light tracking-tight leading-[1.02] text-[clamp(2rem,4.4vw,3.6rem)] max-w-3xl">
                  Wherever you are, is a place to begin.
                </h2>
              </Reveal>
              <div className="mt-14 grid lg:grid-cols-3 gap-6">
                {STAGES.map((s, i) => (
                  <Reveal key={s.era} delay={i * 0.1}>
                    <div className="h-full rounded-sm border border-es-border p-8 bg-es-surface flex flex-col">
                      <span className="font-serif font-light text-es-accent text-4xl">{`0${i + 1}`}</span>
                      <p className="mt-6 text-[11px] uppercase tracking-[0.3em] text-es-muted-fg">{s.era}</p>
                      <h3 className="mt-3 font-serif font-light text-es-fg text-[clamp(1.4rem,2.2vw,1.9rem)] tracking-tight leading-snug">{s.title}</h3>
                      <p className="mt-4 text-es-muted-fg leading-relaxed text-[15px]">{s.body}</p>
                    </div>
                  </Reveal>
                ))}
              </div>

              <Reveal delay={0.1}>
                <div className="mt-8 rounded-sm border border-es-border p-8 lg:p-10 grid lg:grid-cols-12 gap-8 items-center">
                  <div className="lg:col-span-4">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-es-accent mb-3">For families</p>
                    <h3 className="font-serif font-light text-es-fg text-[clamp(1.5rem,2.6vw,2.2rem)] tracking-tight leading-snug">A shared legacy across generations.</h3>
                  </div>
                  <p className="lg:col-span-7 lg:col-start-6 text-es-muted-fg leading-relaxed">
                    EverSoul can also become a shared family legacy environment, allowing multiple generations to
                    contribute memories, stories, photographs, and perspectives — while each person maintains their
                    own privacy and control.
                  </p>
                </div>
              </Reveal>
            </div>
          </section>

          <CrossfadeBand base="/hiw-family.jpg" clean="/hiw-family-clean.jpg" alt="Three generations of a family walking together at sunset" testid="how-band-family" />

          {/* ESTATE PLANNING */}
          <section data-testid="how-estate" className="relative py-24 lg:py-36 bg-es-surface">
            <div className="mx-auto max-w-[1400px] px-6 lg:px-12 grid lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-5">
                <Reveal>
                  <Overline>Where legacy meets estate planning</Overline>
                  <h2 className="font-serif font-light tracking-tight leading-[1.02] text-[clamp(2rem,4.2vw,3.4rem)]">
                    Context you preserve. Authority they keep.
                  </h2>
                </Reveal>
              </div>
              <div className="lg:col-span-6 lg:col-start-7">
                <Reveal delay={0.1}>
                  <p className="text-base sm:text-lg text-es-muted-fg leading-relaxed">
                    As a person's legacy becomes more developed, certain intentions may naturally intersect with
                    estate planning. EverSoul can help organize and preserve the context surrounding those wishes.
                  </p>
                  <p className="mt-6 text-base text-es-muted-fg leading-relaxed">
                    Attorneys, trustees, executors, and other authorized professionals always retain responsibility
                    for legal and fiduciary decisions.
                  </p>
                </Reveal>
              </div>
            </div>
          </section>

          {/* EMOTIONAL CLOSING */}
          <section data-testid="how-closing" className="relative py-32 lg:py-48 bg-[#0B0C0F] text-[#F9F8F6] overflow-hidden">
            <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
              <Reveal>
                <h2 className="font-serif font-light tracking-tight leading-[1.03] text-[clamp(2.2rem,5vw,4.4rem)] max-w-4xl text-balance">
                  Your life is more than what you leave behind.
                </h2>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="mt-12 max-w-2xl space-y-2 font-serif font-light text-[#c8c9cc] text-[clamp(1.2rem,2.4vw,1.9rem)] leading-relaxed">
                  <p>It is the stories behind the decisions.</p>
                  <p>The reasons behind the choices.</p>
                  <p>The people who mattered.</p>
                  <p>The lessons you learned.</p>
                  <p>The values you carried.</p>
                  <p>The things you wanted your family to understand.</p>
                </div>
              </Reveal>
              <Reveal delay={0.25}>
                <p className="mt-12 text-[#e6e2d8] text-lg sm:text-xl max-w-xl">
                  EverSoul gives you a place to begin preserving them.
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <a
                    href="mailto:greg@eversoul.world?subject=Beginning%20my%20EverSoul"
                    data-testid="how-begin-cta"
                    className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#F9F8F6] text-[#0B0C0F] text-sm tracking-wide hover:opacity-90 transition-opacity duration-300"
                  >
                    Begin a Conversation
                  </a>
                  <a
                    href="/"
                    data-testid="how-home-cta"
                    className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/30 text-[#F9F8F6] text-sm tracking-wide hover:border-white transition-colors duration-300"
                  >
                    Back to Home
                  </a>
                </div>
              </Reveal>
            </div>
          </section>
        </main>

        <Footer />
        <Toaster position="bottom-right" />
      </div>
    </SmoothScroll>
  );
}
